import { format } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Stack,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { localeDateFormat } from '../../../utils/localeUtils'
import { EnhancementDay } from '../../../models/enhancement'
import { validateEnhancementHours } from '../../../utils/SharedMethods/handleHoursChange'
import { RootStore } from '../../../redux/store'
import { BankHoliday } from '../../../types/bank-holiday'
import { getCalculationCodeOptions } from '../../../utils/SharedMethods/getCalculationCodeOptions'
import { getDefaultCalculationCode } from '../../../utils/SharedMethods/getDefaultCalculationCode'

interface DaysCheckboxListProps {
  days: EnhancementDay[]
  disableCheckbox: boolean
  disableTextField: boolean
  onDaysChange: (updatedDays: EnhancementDay[]) => void
  error?: (day: EnhancementDay) => boolean
  employeeLocationId?: number
  employeeLocationGroupId?: number
}

const handleEnhancementHoursChange = (
  hours: string,
  selecteday: Date,
  enhancementDays: EnhancementDay[]
): EnhancementDay[] =>
  enhancementDays.map(day => {
    if (new Date(day.day!).getTime() === selecteday.getTime()) {
      const validated = validateEnhancementHours(hours, day.checked)
      return {
        ...day,
        hours: validated.hours,
        checked: validated.checked,
      }
    }
    return day
  })

export default function DaysCheckboxList({
  days,
  disableCheckbox,
  disableTextField,
  onDaysChange,
  error,
  employeeLocationId,
  employeeLocationGroupId,
}: DaysCheckboxListProps) {
  const [enhancementDays, setEnhancementDays] = useState<EnhancementDay[]>(days)
  const allBankHolidays = useSelector<RootStore, BankHoliday[]>(
    (state: RootStore) => state.appSettings.allBankHolidays
  )
  const { enhancementsRequestTypeConfig } = useSelector((state: RootStore) => state.appSettings)
  const useCalculationCodes = enhancementsRequestTypeConfig?.useCalculationCodes === true

  const bankHolidays = useMemo(() => {
    if (!employeeLocationId || !allBankHolidays) return []
    return allBankHolidays.filter(bh => bh.locationId === employeeLocationId)
  }, [allBankHolidays, employeeLocationId])

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const updatedDays = [...enhancementDays]
    updatedDays[index].checked = e.target.checked
    setEnhancementDays(updatedDays)
    onDaysChange(updatedDays)
  }

  const handleHoursChanged = (hours: string, day: EnhancementDay) => {
    const updatedDays = handleEnhancementHoursChange(hours, new Date(day.day), enhancementDays)
    setEnhancementDays(updatedDays as EnhancementDay[])
    onDaysChange(updatedDays as EnhancementDay[])
  }

  useEffect(() => {
    setEnhancementDays(days)
  }, [days])
  return (
    <Grid container gap={3}>
      {enhancementDays?.map((day, index) => (
        <Grid item xs={12} key={day?.day?.toString()}>
          <Stack direction="row" gap={2}>
            <Checkbox
              checked={day.checked}
              onChange={e => handleCheckboxChange(e, index)}
              disabled={disableCheckbox}
            />
            {day.calculationCode ? (
              <FormControl fullWidth>
                <InputLabel id={`day-input-${day.day}-label`} shrink>
                  {format(new Date(day.day!), `ccc ${localeDateFormat}`)}
                </InputLabel>
                <Select
                  id={`day-input-${day.day}`}
                  labelId={`day-input-${day.day}-label`}
                  label={format(new Date(day.day!), `ccc ${localeDateFormat}`)}
                  fullWidth
                  disabled={disableTextField}
                  value={day.calculationCode}
                  onChange={e => {
                    const code = e.target.value as string
                    const updatedDays = enhancementDays.map(d =>
                      new Date(d.day!).getTime() === new Date(day.day!).getTime()
                        ? { ...d, calculationCode: code }
                        : d
                    )
                    setEnhancementDays(updatedDays)
                    onDaysChange(updatedDays)
                  }}
                >
                  {getCalculationCodeOptions(
                    getDefaultCalculationCode(
                      new Date(day.day!),
                      bankHolidays,
                      employeeLocationGroupId,
                      useCalculationCodes
                    )
                  ).map(option => (
                    <MenuItem key={option} value={String(option)}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                id={`day-input-${day.day}`}
                type="number"
                InputProps={{
                  inputProps: { step: '0.25', min: 0.25, max: 24 },
                }}
                fullWidth
                label={format(new Date(day.day!), `ccc ${localeDateFormat}`)}
                value={day.hours}
                error={error?.(day)}
                onChange={e => handleHoursChanged(e.target.value, day)}
                disabled={disableTextField}
              />
            )}
          </Stack>
        </Grid>
      ))}
    </Grid>
  )
}
